// @ts-nocheck - TODO: properly type this file!

import headAvatar from '@/assets/images/HeadAvatar.svg';
import { CustomTitle } from '@/components';
import SearchInput from '@/components/SearchInput';
import { Checkbox, Divider, Empty, List, Space } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { detailsWrap, ellipsisInline, listWrap } from './styles';
import SortableRow from './SortableRow';
import { GetCourierContactsDirectory } from '@/apis/settings';
import { useToken } from '@/common/helper';
import { useSpin } from '@/components/SpinContext';
import { useUnAuthorize } from '@/hooks';
import Flag from './Flag';
import { mapApiContactToContact } from './utils';
// import IconButton from './IconButton';
// import { EditIcon, NoteAdd } from '@/assets/icons/icons';

interface ContactApi {
  id?: string | number;
  label?: string;
  topic?: string;
  courier_name?: string;
  country_code?: string;
  country_region?: string;
  channel?: string;
  operating_hours?: string;
  usage_notes?: string;
  contact_info?: string;
  formatted_phone?: string;
  phone_number?: string;
  email?: string;
  contact_source?: 'directory' | 'custom' | string;
}
type SourceTag = { source: 'directory' | 'details' | 'list' };

export interface Contact {
  id: string | number;
  name?: string;
  email?: string;
  phone?: string;
  hours?: string;
  note?: string;
  country?: string;
  contact_type?: string;
  api?: ContactApi;
  isActive?: boolean;
  source?: 'directory' | 'custom' | string;
}

type Props = {
  providerData: { id?: string | number; courier_id?: string | number };
  onChange?: (payload) => void;
  workingContacts?: Contact[];
  onEdit?: (apiContact: ContactApi, opts?: SourceTag) => void;
  onAddNote?: (contact: Contact, opts?: SourceTag) => void;
};

const DirectContactDirectory: React.FC<Props> = ({
  providerData,
  onChange,
  workingContacts = [],
  // onEdit,
  // onAddNote,
}) => {
  const [items, setItems] = useState<Contact[]>([]);
  const [query, setQuery] = useState('');
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();
  const { showSpin, hideSpin } = useSpin();
  const providerId = providerData?.courier_id ?? providerData?.id;

  interface ContactApi {
    id?: string | number;
    label?: string;
    topic?: string;
    courier_name?: string;
    country_code?: string;
    country_region?: string;
    channel?: string;
    operating_hours?: string;
    usage_notes?: string;
    contact_info?: string;
    formatted_phone?: string;
    phone_number?: string;
    email?: string;
    contact_source?: 'directory' | 'custom' | string;
  }

  // const handleEdit = useCallback(
  //   (contact: Contact) => onEdit?.(contact, { source: 'directory' }),
  //   [onEdit]
  // );

  // const handleNoteAdd = useCallback(
  //   (contact: Contact) => onAddNote?.(contact, { source: 'directory' }),
  //   [onAddNote]
  // );
  interface GetCourierContactsDirectoryResponse {
    data: ContactApi[];
  }
  const isSmallScreen = window.innerWidth <= 576;
  useEffect(() => {
    if (!providerId || !token) return;

    let cancelled = false;
    (async () => {
      try {
        showSpin();
        const res = (await GetCourierContactsDirectory(
          token,
          providerId
        )) as GetCourierContactsDirectoryResponse;

        const apiContacts: ContactApi[] = res.data ?? [];
        const mapped = apiContacts.map(mapApiContactToContact);
        const defaultContacts = mapped
          .filter((contact) => contact.default != true)
          .map((contact) => ({
            ...contact,
            api: {
              ...contact.api,
              editable: false,
            },
          }));

        setItems(defaultContacts as Contact[]);
      } catch (err) {
        if (!cancelled) unauthorize(err);
      } finally {
        if (!cancelled) hideSpin();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [providerId, token, showSpin, hideSpin, unauthorize]);
  const checkedIdSet = useMemo(() => {
    return new Set((workingContacts ?? []).map((c) => String(c.id)));
  }, [workingContacts]);

  useEffect(() => {
    setItems((prev) =>
      (prev ?? []).map((c) => ({
        ...c,
        isActive: checkedIdSet.has(String(c.id)),
      }))
    );
  }, [checkedIdSet, items.length]);

  const toggleActive = useCallback(
    (id: string | number, checked: boolean) => {
      setItems((prev) => {
        const next = (prev ?? []).map((c) =>
          c.id === id ? { ...c, isActive: checked } : c
        );

        const contact = next.find((c) => String(c.id) === String(id));
        if (contact) {
          onChange?.({
            id: String(id),
            action: checked ? 'add' : 'remove',
            contact,
          });
        }

        return next;
      });
    },
    [onChange]
  );

  const lc = (v: string | undefined | null) =>
    (v ?? '').toString().toLowerCase();

  const digitsOnly = (v: string | undefined | null) =>
    (v ?? '').toString().replace(/\D/g, '');

  const isFiltering = query.trim().length > 0;
  const visible = useMemo(() => {
    if (!isFiltering) return items ?? [];
    const q = query.trim().toLowerCase();
    const qDigits = digitsOnly(query);

    return (items ?? []).filter((c) => {
      const api: ContactApi = c.api ?? {};

      const textFields: string[] = [
        c.name ?? '',
        c.email ?? '',
        c.phone ?? '',
        c.hours ?? '',
        c.note ?? '',
        c.country ?? '',
        c.contact_type ?? '',
        api.label ?? '',
        api.topic ?? '',
        api.courier_name ?? '',
        api.country_code ?? '',
        api.country_region ?? '',
        api.channel ?? '',
        api.operating_hours ?? '',
        api.usage_notes ?? '',
        api.contact_info ?? '',
        api.formatted_phone ?? '',
      ];

      const textHit = textFields.some((v) => lc(v).includes(q));

      const phoneCandidates: string[] = [
        c.phone ?? '',
        api.phone_number ?? '',
        api.formatted_phone ?? '',
        api.contact_info ?? '',
      ];

      const phoneHit =
        qDigits.length > 0 &&
        phoneCandidates.some((p) => digitsOnly(p).includes(qDigits));

      return textHit || phoneHit;
    });
  }, [items, isFiltering, query]);

  const list = (
    <List
      rowKey={(item) => String((item as Contact).id)}
      itemLayout='vertical'
      dataSource={visible}
      style={{ overflowX: 'hidden' }}
      renderItem={(item) => (
        <SortableRow id={String(item.id)} showDrag={false}>
          <Checkbox
            checked={item.isActive === true}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => toggleActive(item.id, e.target.checked)}
            aria-label='Select contact'
          />

          <img src={headAvatar} alt='' />

          <div style={detailsWrap}>
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <CustomTitle
                text={item.name}
                fontSize='12px'
                lineHeight='16px'
                fontWeight={600}
                color='black'
                style={ellipsisInline}
              />
            </div>

            {(() => {
              const hasEmail = !!item.email;
              const rawPhone =
                item.phone ||
                item.api?.formatted_phone ||
                item.api?.phone_number;
              const hasPhone = !!rawPhone;

              return (
                <div
                  style={{
                    display: 'flex',
                    gap: 0,
                    flexWrap: 'wrap',
                    minWidth: 0,
                    wordBreak: 'break-word',
                  }}
                >
                  {hasEmail && (
                    <CustomTitle
                      text={item.email}
                      fontSize='12px'
                      lineHeight='16px'
                      marginBottom='0'
                      fontWeight={400}
                      style={ellipsisInline}
                    />
                  )}

                  {hasEmail && hasPhone && (
                    <span aria-hidden='true' style={{ padding: '0 6px' }}>
                      ,
                    </span>
                  )}

                  {hasPhone && (
                    <CustomTitle
                      text={
                        <span
                          title={rawPhone}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <Flag
                            country={
                              item.country ||
                              item.api?.country_code ||
                              item.api?.country_region ||
                              ''
                            }
                          />
                          {rawPhone}
                        </span>
                      }
                      fontSize='12px'
                      lineHeight='16px'
                      fontWeight={400}
                      style={ellipsisInline}
                    />
                  )}
                </div>
              );
            })()}

            {item.hours && (
              <CustomTitle
                text={item.hours}
                fontSize='12px'
                lineHeight='16px'
                fontWeight={400}
                style={ellipsisInline}
              />
            )}

            {item.note && (
              <div style={{ marginTop: 6 }}>
                <span
                  style={{
                    display: 'inline-block',
                    background: '#E3E3E3',
                    padding: '4px 8px',
                    fontWeight: 600,
                    borderRadius: 4,
                    fontSize: 10,
                    lineHeight: '16px',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {item.note}
                </span>
              </div>
            )}
          </div>

          <Space style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {/* <>
              <IconButton
                ariaLabel='Edit contact'
                onClick={() => handleEdit(item)}
              >
                <EditIcon
                  size={16}
                  style={{ position: 'relative', top: '3px' }}
                />
              </IconButton>

              <IconButton
                ariaLabel='Add note'
                onClick={() => handleNoteAdd(item)}
              >
                <NoteAdd />
              </IconButton>
            </> */}
          </Space>
        </SortableRow>
      )}
    />
  );

  return (
    <div style={{ width: '100%' }}>
      <SearchInput
        width='100%'
        placeholder='Search...'
        onChange={(val: string) => setQuery(val)}
        onClear={() => setQuery('')}
      />
      <Divider style={{ margin: '8px 0 12px' }} />

      {(visible?.length ?? 0) === 0 ? (
        <Empty description='No contacts' />
      ) : (
        <div style={listWrap(isSmallScreen)}>{list}</div>
      )}
    </div>
  );
};

export default DirectContactDirectory;
